import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    CSSObject,
    List,
    Theme,
    styled,
} from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import Cookies from "js-cookie";
import { useState } from "react";
import SidebarItemCollapse from "../../components/SidebarItemCollapse";
import SlidebarItemWithOutCollapse from "../../components/SlidebarItemWithOutCollapse";
import sizeConfigs from "../../config/sizeConfigs";
import useAccessControl from "../../hooks/useAccessControl";
import { appAdminRoutes } from "../routes/adminRoutes";
import { appAdminSettingRoutes } from "../routes/adminSettingRoutes";

type Props = {
    handleScreen: () => void;
};

const openedMixin = (theme: Theme): CSSObject => ({
    width: sizeConfigs.sidebar.width,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: "0",
    [theme.breakpoints.up("sm")]: {
        width: "0",
    },
});


const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    width: sizeConfigs.sidebar.width,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
    }),
}));


export const AdminSettingsSidebar = ({ handleScreen }: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { permissions }: any = useAccessControl();
    const [open, setOpen] = useState<boolean>(true);
    const role = Cookies.get("role-velox-charter") || "";
    const isAdmin = role.toLowerCase() === "admin";

    const normalize = (v: string) =>
        v.replace(/\s+/g, "").replace(/-/g, "").toLowerCase();

    const filteredSettingsItems = appAdminSettingRoutes.filter((item) => {
        if (isAdmin) return true;

        const parentKey = normalize(item.section);

        if (permissions?.[parentKey]) return true;

        if (item.child) {
            return item.child.some((child: any) => {
                const childKey = normalize(child.section);
                return permissions?.[childKey] === true;
            });
        }

        return false;
    });

    const hasAnySidebarPermission = appAdminRoutes.some((item) => {
        const key = normalize(item.section);
        return permissions?.[key] === true;
    });

    const showGoBackButton = isAdmin || hasAnySidebarPermission;

    const firstAllowedAdminRoute = appAdminRoutes.find((item) => {
        if (isAdmin) return true;

        return permissions?.[normalize(item.section)];
    });

    const firstAllowedSettingRoute = appAdminSettingRoutes
        .flatMap((item: any) => item.child || [])
        .find((item: any) => permissions?.[normalize(item.section)]);

    const goBackLink = isAdmin
        ? "/admin/dashboard"
        : firstAllowedAdminRoute?.path ||
        firstAllowedSettingRoute?.path ||
        "/unauthorized";

    return (
        <>
            <Drawer
                variant="permanent"
                open={open}
                sx={{
                    width: sizeConfigs.sidebar.width,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        top: "100px",
                        height: "calc(100vh - 100px)",
                        position: "fixed",
                        overflow: "hidden",
                        backgroundColor: "#fff",
                        borderRight: "1px solid #D0D0D0",
                    }
                }}
            >
                <List
                    disablePadding
                    sx={{
                        overflowY: "auto",
                        paddingTop: "10px",
                        paddingBottom: "60px",
                    }}
                >
                    <div className="flex w-full px-[10px] mt-[10px] justify-end items-center border-b">
                        <span className={`material-symbols-outlined text-[30px] text-black hover:cursor-pointer`} onClick={() => {
                            setOpen(!open);
                            handleScreen();
                        }}>
                            arrow_left
                        </span>
                    </div>

                    <div className="w-full flex pt-[10px] flex-col items-start gap-1.5">
                        {filteredSettingsItems.map((route: any, index) => {
                            if (isAdmin) {
                                return route.sidebarProps && route.child ? (
                                    <SidebarItemCollapse item={route} key={index} />
                                ) : (
                                    <SlidebarItemWithOutCollapse item={route} key={index} />
                                );
                            }
                            const hasAccessSection = route.child
                                ? route.child.some((child: any) => permissions[normalize(child.section)] === true)
                                : permissions[normalize(route.section)];

                            const dashboardHasAccess = isAdmin || hasAccessSection;

                            if (dashboardHasAccess) {
                                return route.sidebarProps && route.child
                                    ? <SidebarItemCollapse
                                        item={{
                                            ...route,
                                            child: route.child?.filter((child: any) =>
                                                isAdmin || permissions[normalize(child.section)]
                                            )
                                        }}
                                        key={index}
                                    />
                                    : <SlidebarItemWithOutCollapse item={route} key={index} />;
                            }

                            return null;
                        })}

                    </div>
                </List>

                <div className={`w-full mt-auto`}>
                    {showGoBackButton && (
                        <div
                            className="absolute bottom-0 left-0 w-full bg-white border-t border-[#D0D0D0]"
                            style={{ zIndex: 1 }}
                        >
                            <div
                                className="w-full flex items-center gap-2.5 p-[10px] cursor-pointer hover:bg-primary text-black hover:text-white"
                                onClick={() => navigate(goBackLink)}
                            >
                                <span className="material-symbols-outlined text-[25px]">
                                    arrow_back
                                </span>

                                <span className="text-[13px] font-medium tracking-wide">
                                    Go Back
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </Drawer>

            {!open && (
                <div
                    className="sidebar close bg-white h-[calc(100vh-80px)] relative flex flex-col border-r border-[#D0D0D0]"
                >
                    <div className={`flex items-center justify-center cursor-pointer text-black hover:text-white hover:bg-primary border-b`}>
                        <span className={`material-symbols-outlined text-inherit text-[30px]`}
                            onClick={() => {
                                setOpen(!open);
                                handleScreen();
                            }}>
                            arrow_right
                        </span>
                    </div>

                    <ul className="nav-links max-h-[calc(100vh - 50px)]">
                        {filteredSettingsItems?.map((item: any, index: number) => {
                            const hasCollapseAccessSection = item.child
                                ? item.child.some((child: any) => isAdmin || permissions[normalize(child.section)])
                                : permissions[normalize(item.section)];
                            const dashboardHasAccess = isAdmin || hasCollapseAccessSection;
                            let isActive = !item?.child ? location.pathname.startsWith(item?.path) ? true : false : false

                            if (dashboardHasAccess) {
                                return (
                                    item?.child
                                        ?
                                        <li key={index} className={`flex items-center justify-center p-[10px] cursor-pointer hover:bg-primary text-black hover:text-white`}>
                                            <div className="iocn-link">
                                                <span className={`material-symbols-outlined text-inherit text-[20px] `}>
                                                    {item?.sidebarProps?.icon}
                                                </span>

                                            </div>

                                            <ul className={`sub-menu bg-white rounded border border-solid border-[#D0D0D0]`}>
                                                <li className={`text-[#000000] font-semibold cursor-default`}>
                                                    <span className="link_name">
                                                        {item?.sidebarProps?.displayText}
                                                    </span>
                                                </li>

                                                {item?.child?.map((childItem: any, i: number) => {
                                                    const hasAccessChild = childItem?.section
                                                        ? (isAdmin || permissions[normalize(childItem.section)])
                                                        : false;
                                                    const isChildActive = location.pathname === childItem.path;
                                                    if (hasAccessChild) {

                                                        return (
                                                            <li key={i} className={`${isChildActive ? "bg-primary text-white" : "bg-white text-black"}  hover:bg-primary hover:text-white font-medium cursor-pointer`}>
                                                                <Link to={childItem?.path}>
                                                                    {childItem?.sidebarProps?.displayText}
                                                                </Link>
                                                            </li>
                                                        )
                                                    }

                                                    return null;
                                                })}
                                            </ul>
                                        </li>
                                        :
                                        <li key={index} className={`flex items-center justify-center p-[10px] cursor-pointer hover:bg-primary text-black hover:text-white`}>
                                            <span>
                                                <Link to={item?.path}>
                                                    <span className={`material-symbols-outlined text-inherit text-[20px]`}>
                                                        {item?.sidebarProps?.icon}
                                                    </span>
                                                </Link>
                                            </span>
                                            <ul className={`sub-menu blank bg-white rounded border border-solid border-[#D0D0D0]`}>
                                                <li className={`${isActive ? "bg-primary text-white" : "bg-white text-black"} hover:bg-primary hover:text-white font-medium cursor-pointer`}>
                                                    <Link to={item?.path} className="link_name">
                                                        {item?.sidebarProps?.displayText}
                                                    </Link>
                                                </li>
                                            </ul>
                                        </li>
                                )
                            }

                            return null;
                        })}


                    </ul>
                    {showGoBackButton && (
                        <div
                            className="absolute bottom-0 left-0 w-full bg-white border-t border-[#D0D0D0]"
                            style={{ zIndex: 1 }}
                        >
                            <div
                                className="w-full flex items-center gap-2.5 p-[10px] cursor-pointer hover:bg-primary text-black hover:text-white"
                                onClick={() => navigate(goBackLink)}
                            >
                                <span className="material-symbols-outlined text-[25px]">
                                    arrow_back
                                </span>
                            </div>
                        </div>
                    )}
                </div>

            )}
        </>
    );
};
